import { faCalendarDays, faChair, faMoneyBillWave, faShoppingCart } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Stack, Typography } from '@vapor/material'
import { t } from 'i18next'
import { useMemo } from 'react'
import { Currency } from 'src/api/currency'
import { HiCIcon } from 'src/app/components/icons/icon'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import { ToolbarButton } from 'src/app/layout/form-layout/form-layout.types'
import { isValidDate } from 'src/utils/date'

export type ReservationTimeNewToolbarProps = {
  confirmButton?: ToolbarButton
  cancelButton?: ToolbarButton
  confirmAndOpenButton?: ToolbarButton
  dateRange: { checkIn: Date | null; checkOut: Date | null }
  resources: number
  /** Average daily recalculated */
  averageDailyPrice: number
  /** Total price of accomodation*/
  totalAccomodationPrice: number
  currency: Currency
}

export default function ReservationTimeNewToolbar(props: ReservationTimeNewToolbarProps) {
  const formatDate = useFormatDate()
  const { formatCurrency } = useFormatCurrency()
  const { sm } = useBreakpoints()

  const dateRange: { checkIn: Date; checkOut: Date } = useMemo(() => {
    if (isValidDate(props.dateRange.checkIn) && isValidDate(props.dateRange.checkOut)) {
      return { checkIn: props.dateRange.checkIn ?? new Date(), checkOut: props.dateRange.checkOut ?? new Date() }
    } else {
      return { checkIn: new Date(), checkOut: new Date() }
    }
  }, [props.dateRange.checkIn, props.dateRange.checkOut])

  const totalPrice = useMemo(() => {
    return formatCurrency(props.totalAccomodationPrice, props.currency)
  }, [props.totalAccomodationPrice, props.currency, formatCurrency])

  return (
    <div style={{ width: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {sm && (
            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2} sx={{ backgroundColor: 'white', borderRadius: '0.5rem', width: 'auto', p: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <HiCIcon>
                  <FontAwesomeIcon icon={faCalendarDays} />
                </HiCIcon>
                <Typography variant="caption">{`${formatDate(dateRange.checkIn ?? new Date())} - ${formatDate(dateRange.checkOut ?? new Date())}`}</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <HiCIcon>
                  <FontAwesomeIcon icon={faShoppingCart} />
                </HiCIcon>
                <Typography variant="caption">{props.resources}</Typography>
              </Stack>
              <Stack direction="row" alignItems="center">
                <HiCIcon sx={{ display: { xs: 'none', sm: 'inherit' } }}>
                  <FontAwesomeIcon icon={faMoneyBillWave} style={{ marginRight: 5 }} />
                </HiCIcon>
                <Typography variant="caption">
                  <b>{totalPrice}</b>
                </Typography>
              </Stack>
            </Stack>
          )}
        </Stack>
        <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
          {props.cancelButton?.clickHandler && (
            <Button size={'small'} variant="outlined" onClick={props.cancelButton.clickHandler} disabled={props.cancelButton.disabled}>
              {props.cancelButton.text ?? t('commons.cancel')}
            </Button>
          )}
          {props.confirmButton?.clickHandler && (
            <Button size={'small'} variant="outlined" onClick={props.confirmButton.clickHandler} disabled={props.confirmButton.disabled}>
              {props.confirmButton.text ?? t('commons.save')}
            </Button>
          )}
          {props.confirmAndOpenButton?.clickHandler && (
            <Button size={'small'} variant="contained" onClick={props.confirmAndOpenButton.clickHandler} disabled={props.confirmAndOpenButton.disabled}>
              {props.confirmAndOpenButton.text ?? t('commons.save')}
            </Button>
          )}
        </Stack>
      </Stack>
    </div>
  )
}
